import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const SaveSetupDialog = ({ results, initialBalance, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handleSave = async () => {
    if (!results || !results.steps || results.steps.length === 0) {
      toast.error('Нет данных для сохранения');
      return;
    }

    setSaving(true);

    try {
      // Format steps for backend - filter out error steps and validate data
      const steps = results.steps
        .filter(step => !step.error) // Remove error steps
        .map(step => ({
          direction: step.direction,
          entry_price: parseFloat(step.entry) || 0,
          exit_price: parseFloat(step.exit) || 0,
          leverage: parseInt(step.leverage) || 1,
          p_liq_percent: parseFloat(step.p_liq) || 0,
          pnl_usd: parseFloat(step.PnL_USD) || 0,
          liquidation_price: parseFloat(step.liquidation_price) || 0,
          balance_after: parseFloat(step.balance_after) || 0,
          max_btc_amount: parseFloat(step.max_btc_amount) || 0,
          reached: false
        }));

      if (steps.length === 0) {
        toast.error('Нет валидных шагов для сохранения');
        return;
      }

      // Generate default title if not provided
      const finalTitle = title.trim() || `Сетап ${new Date().toLocaleDateString('ru-RU')} ${new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;

      const setupData = {
        title: finalTitle,
        initial_balance: parseFloat(initialBalance) || 0,
        steps: steps,
        is_public: isPublic
      };

      const response = await axios.post(
        `${backendUrl}/api/setups`,
        setupData,
        { withCredentials: true }
      );

      toast.success('Сетап успешно сохранён!');
      setOpen(false);
      setTitle('');
      setIsPublic(false);
      
      if (onSaved) {
        onSaved(response.data);
      }
    } catch (error) {
      console.error('Error saving setup:', error);
      if (error.response?.status === 401) {
        toast.error('Для сохранения сетапов необходимо войти через Google', {
          description: 'Нажмите "Войти" в правом верхнем углу'
        });
      } else {
        toast.error('Ошибка сохранения сетапа');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black hover:opacity-90 font-semibold"
          disabled={!results || !results.steps || results.steps.length === 0}
        >
          <Save className="h-4 w-4 mr-2" />
          Сохранить сетап
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#1a1a1a] border-gray-800 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">Сохранить сетап</DialogTitle>
          <DialogDescription className="text-gray-400">
            Сохраните ваш сетап, чтобы отслеживать его прогресс
          </DialogDescription>
        </DialogHeader>
        
        {/* Setup Summary */}
        {results && results.steps && results.steps.length > 0 && (
          <div className="bg-[#0a0a0a] rounded-lg p-4 border border-gray-800">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-400 mb-1">Вложения</p>
                <p className="text-lg font-semibold text-white">${initialBalance.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Потенциал</p>
                <p className="text-lg font-semibold text-[#00ff88]">
                  ${results.steps[results.steps.length - 1].balance_after.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Шагов</p>
                <p className="text-lg font-semibold text-white">{results.steps.length}</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="title" className="text-white">Название сетапа (опционально)</Label>
            <Input
              id="title"
              placeholder="Например: BTC Breakout Strategy"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-2 bg-[#0a0a0a] border-gray-800 text-white"
              disabled={saving}
            />
            <p className="text-xs text-gray-500 mt-1">
              Если не указано, будет сгенерировано автоматически
            </p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="public" className="text-white">Публичный сетап</Label>
              <p className="text-sm text-gray-400 mt-1">
                Публичные сетапы видны в лидерборде
              </p>
            </div>
            <Switch
              id="public"
              checked={isPublic}
              onCheckedChange={setIsPublic}
              disabled={saving}
            />
          </div>
          <div className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1 border-gray-800 text-gray-400 hover:text-white hover:bg-gray-800"
              disabled={saving}
            >
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black hover:opacity-90 font-semibold"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Сохранение...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Сохранить
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SaveSetupDialog;
